import React from 'react';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import { useAccessTheme } from '../useAccessTheme';

export function EntraSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  const t = useAccessTheme();
  return (
    <div className={`rounded-xl border p-4 ${t.tableShell}`}>
      <h4 className={`text-sm font-semibold ${t.title}`}>{title}</h4>
      {description ? <p className={`text-sm mt-1 ${t.muted}`}>{description}</p> : null}
      <div className={description || title ? 'mt-3' : ''}>{children}</div>
    </div>
  );
}

export function EntraToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const t = useAccessTheme();
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className={`text-sm font-medium ${t.title}`}>{label}</div>
        {description ? <div className={`text-xs mt-0.5 ${t.muted}`}>{description}</div> : null}
      </div>
      <button type="button" onClick={() => onChange(!checked)} aria-pressed={checked}>
        {checked ? (
          <ToggleRight className={'w-8 h-8 ' + t.toggleOn} />
        ) : (
          <ToggleLeft className={'w-8 h-8 ' + t.toggleOff} />
        )}
      </button>
    </div>
  );
}

export function EntraRadioOption<T extends string>({
  name,
  value,
  checked,
  title,
  description,
  onChange,
}: {
  name: string;
  value: T;
  checked: boolean;
  title: string;
  description?: string;
  onChange: (value: T) => void;
}) {
  const t = useAccessTheme();
  return (
    <label className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${t.border} ${checked ? t.badgeAccent : ''}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <span>
        <span className={`block text-sm font-medium ${t.title}`}>{title}</span>
        {description ? <span className={`block text-xs mt-0.5 ${t.muted}`}>{description}</span> : null}
      </span>
    </label>
  );
}

export function EntraDetailGrid({ items }: { items: { label: string; value: React.ReactNode }[] }) {
  const t = useAccessTheme();
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((item) => (
        <div key={item.label}>
          <dt className={`text-xs ${t.muted}`}>{item.label}</dt>
          <dd className={`text-sm font-medium mt-0.5 ${t.title}`}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EntraCheckbox({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}) {
  const t = useAccessTheme();
  return (
    <label className={`flex items-center gap-2 text-sm ${disabled ? t.muted : t.body}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      {label}
    </label>
  );
}

export function MicrosoftMark() {
  return (
    <span className="inline-grid grid-cols-2 gap-px w-3.5 h-3.5" aria-hidden>
      <span className="bg-[#F25022]" />
      <span className="bg-[#7FBA00]" />
      <span className="bg-[#00A4EF]" />
      <span className="bg-[#FFB900]" />
    </span>
  );
}
